import { motion } from "framer-motion";
import { Briefcase, Cpu, GraduationCap, ArrowUpRight } from "lucide-react";

const pillars = [
  {
    icon: Briefcase,
    title: "Tax Practice",
    subtitle: "The Strategy Layer",
    accent: "#16D1C8",
    points: [
      "Corporate Tax Advisory & Structuring",
      "International Taxation & Transfer Pricing",
      "RBI Compliances — APR & FLA Filings",
      "Cross-border Tax Structuring",
      "GST Compliance & IT Notice Resolution",
    ],
    badge: "For Corporates · HNIs · NRIs",
  },
  {
    icon: Cpu,
    title: "Technology",
    subtitle: "The Builder Layer",
    accent: "#FF2D7A",
    points: [
      "TaxSetu.ai — AI-powered Income Tax Research",
      "PractiQ — India's first CA Job Simulator",
      "Built for CAs, students & practitioners",
      "1800+ queries resolved on TaxSetu.ai",
    ],
    badge: "For the Profession",
  },
  {
    icon: GraduationCap,
    title: "Faculty",
    subtitle: "The Mentor Layer",
    accent: "#FF8A3D",
    points: [
      "Unacademy — Int'l Taxation, CA Final (formerly)",
      "Physics Wallah — Income Tax Faculty",
      "Bajaj Capital ICOFP — CFP Level 2",
      "ICAI Sessions & Corporate Workshops",
      "YouTube — Simplifying tax for everyone",
    ],
    badge: "For the Next Generation",
  },
];

export const Pillars = () => {
  return (
    <section id="pillars" data-testid="pillars-section" className="relative section-pad overflow-hidden">
      <div className="container-x relative z-10">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-100px" }}
          transition={{ duration: 0.6 }}
          className="max-w-3xl"
        >
          <div className="label-eyebrow mb-5">Three layers · one mission</div>
          <h2 data-testid="pillars-title" className="font-heading font-bold text-4xl md:text-5xl lg:text-6xl leading-[1.05]">
            Where strategy meets <br />
            <span className="gradient-text">technology &amp; teaching.</span>
          </h2>
          <p className="mt-6 text-white/65 max-w-2xl">
            Three distinct domains, one operating philosophy — clarity, judgment, and outcomes.
          </p>
        </motion.div>

        <div className="mt-16 grid lg:grid-cols-3 gap-6">
          {pillars.map((p, idx) => (
            <motion.div
              key={p.title}
              data-testid={`pillar-card-${idx}`}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-80px" }}
              transition={{ duration: 0.6, delay: idx * 0.1 }}
              className="group glass p-8 relative overflow-hidden hover:-translate-y-1 transition-all duration-500"
              style={{ borderColor: "rgba(255,255,255,0.08)" }}
            >
              <div
                className="absolute -top-24 -right-24 w-56 h-56 rounded-full opacity-0 group-hover:opacity-50 blur-3xl transition-opacity duration-500"
                style={{ background: p.accent }}
              />
              <div className="relative z-10">
                <div className="flex items-center justify-between">
                  <div className="w-14 h-14 rounded-2xl grid place-items-center border border-white/10"
                       style={{ background: `${p.accent}14` }}>
                    <p.icon size={22} style={{ color: p.accent }} />
                  </div>
                  <span className="text-[10px] uppercase tracking-[0.25em] text-white/40">{p.badge}</span>
                </div>
                <div className="mt-7">
                  <div className="text-xs uppercase tracking-widest" style={{ color: p.accent }}>{p.subtitle}</div>
                  <h3 className="font-heading font-semibold text-3xl mt-1.5 text-white">{p.title}</h3>
                </div>
                <ul className="mt-6 space-y-3">
                  {p.points.map((pt) => (
                    <li key={pt} className="flex items-start gap-2.5 text-sm text-white/70">
                      <span className="mt-1.5 w-1.5 h-1.5 rounded-full shrink-0" style={{ background: p.accent }} />
                      <span>{pt}</span>
                    </li>
                  ))}
                </ul>
              </div>
              <div className="absolute bottom-0 left-0 right-0 h-1 opacity-30 group-hover:opacity-100 transition-opacity"
                   style={{ background: `linear-gradient(90deg, transparent, ${p.accent}, transparent)` }} />
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};
